import React, { useState } from 'react';
import { Disclosure, Switch } from '@headlessui/react';
import { ChevronDownIcon, LockClosedIcon, AdjustmentsHorizontalIcon, QuestionMarkCircleIcon } from '@heroicons/react/20/solid';
import {
  MapPinIcon,
  UsersIcon,
  TagIcon,
  ShoppingCartIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  CpuChipIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  IdentificationIcon,
  ClipboardDocumentIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import { DSelect, DSelectOption } from '../../../components/ui/Dashboard/DSelect';
import { DInput } from '../../../components/ui/Dashboard/DInput';
import { DButton } from '../../../components/ui/Dashboard/DButton';

interface FilterSectionProps {
  title: string;
  icon: React.ElementType;
  isLocked?: boolean;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function FilterSection({ title, icon: Icon, isLocked = false, defaultOpen = false, children }: FilterSectionProps) {
  return (
    <Disclosure defaultOpen={defaultOpen}>
      {({ open }) => (
        <div className="border-b border-slate-200">
          <Disclosure.Button className="flex w-full items-center justify-between py-3 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 flex items-center justify-center rounded text-brand-700 bg-brand-50">
                <Icon className="h-3.5 w-3.5" />
              </div>
              <span>{title}</span>
            </div>
            <div className="flex items-center gap-2">
              {isLocked && <LockClosedIcon className="h-4 w-4 text-slate-400" />}
              <ChevronDownIcon className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </div>
          </Disclosure.Button>
          <Disclosure.Panel className="px-4 pb-4 pt-2">
            {children}
          </Disclosure.Panel>
        </div>
      )}
    </Disclosure>
  );
}

const revenueOptions: DSelectOption[] = [
  { id: '1', name: '$1M - $10M' },
  { id: '2', name: '$10M - $50M' },
  { id: '3', name: '$50M - $100M' },
];

export default function CompanyFilterSidebar() {
  const [mustIncludeKeywords, setMustIncludeKeywords] = useState(false);
  const [includeSocialKeywords, setIncludeSocialKeywords] = useState(false);
  const [includeSeoKeywords, setIncludeSeoKeywords] = useState(false);
  const [includeUnknownRevenue, setIncludeUnknownRevenue] = useState(false);
  
  const [selectedMinRev, setSelectedMinRev] = useState<DSelectOption>();
  const [selectedMaxRev, setSelectedMaxRev] = useState<DSelectOption>();
  const [selectedMinFunding, setSelectedMinFunding] = useState<DSelectOption>();
  const [selectedMaxFunding, setSelectedMaxFunding] = useState<DSelectOption>();

  return (
    <div className="flex h-full w-full flex-col bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 p-4 sticky top-0 bg-white z-10">
        <h2 className="text-base font-semibold text-slate-900">Filters</h2>
        <DButton className="text-sm font-semibold text-brand-700 hover:text-brand-800 transition-colors bg-transparent border-none shadow-none px-0">Clear all</DButton>
      </div>

      <div className="flex-1 overflow-y-auto">
        <FilterSection title="Location" icon={MapPinIcon}>
          <DInput placeholder="Enter location" />
        </FilterSection>

        <FilterSection title="Employee Headcount" icon={UsersIcon}>
          <div className="text-sm text-slate-500 p-2">Select headcount range</div>
        </FilterSection>

        <FilterSection title="Industry & Keyword" icon={TagIcon}>
          <div className="space-y-4">
            <DInput 
              placeholder="Search industries..." 
              rightIcon={<AdjustmentsHorizontalIcon className="h-5 w-5 hover:text-brand-600 cursor-pointer transition-colors" />} 
            />
            
            <Disclosure>
              {({ open }) => (
                <div className="border border-slate-200 rounded-md shadow-sm">
                   <Disclosure.Button className="flex w-full items-center justify-between px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-md transition-colors">
                     <span>Company keyword</span>
                     <ChevronDownIcon className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
                   </Disclosure.Button>
                   <Disclosure.Panel className="p-3 border-t border-slate-200 space-y-3.5 bg-slate-50/50">
                     <DInput 
                        placeholder="Enter keyword (3-50 chars)" 
                        rightIcon={<AdjustmentsHorizontalIcon className="h-5 w-5 hover:text-brand-600 cursor-pointer transition-colors" />} 
                      />
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm text-slate-700 leading-tight">Must include all selected keywords</span>
                        <Switch checked={mustIncludeKeywords} onChange={setMustIncludeKeywords} className={`${mustIncludeKeywords ? 'bg-brand-600' : 'bg-slate-200'} relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1`}>
                          <span className={`${mustIncludeKeywords ? 'translate-x-5' : 'translate-x-1'} inline-block h-3 w-3 transform rounded-full bg-white transition-transform`} />
                        </Switch>
                      </div>
                      <label className="flex items-start gap-2.5 cursor-pointer group">
                        <input type="checkbox" checked={includeSocialKeywords} onChange={() => setIncludeSocialKeywords(!includeSocialKeywords)} className="mt-0.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer" />
                        <span className="text-sm text-slate-600 group-hover:text-slate-900 transition-colors leading-tight">Include company social media keywords</span>
                      </label>
                      <label className="flex items-start gap-2.5 cursor-pointer group">
                        <input type="checkbox" checked={includeSeoKeywords} onChange={() => setIncludeSeoKeywords(!includeSeoKeywords)} className="mt-0.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer" />
                        <span className="text-sm text-slate-600 group-hover:text-slate-900 transition-colors leading-tight">Include company SEO keywords</span>
                      </label>
                   </Disclosure.Panel>
                </div>
              )}
            </Disclosure>

            <Disclosure>
               {({ open }) => (
                 <div className="border border-slate-200 rounded-md shadow-sm">
                   <Disclosure.Button className="flex w-full items-center justify-between px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-md transition-colors">
                     <span>NAICS & SIC codes</span>
                     <ChevronDownIcon className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
                   </Disclosure.Button>
                   <Disclosure.Panel className="p-3 border-t border-slate-200 space-y-3 bg-slate-50/50">
                      <DInput placeholder="Enter NAICS" />
                      <a href="#" className="flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700 hover:underline"><QuestionMarkCircleIcon className="h-4 w-4" /> What's NAICS?</a>
                      <DInput placeholder="Enter SIC" />
                      <a href="#" className="flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700 hover:underline"><QuestionMarkCircleIcon className="h-4 w-4" /> What's SIC?</a>
                   </Disclosure.Panel>
                 </div>
               )}
            </Disclosure>
          </div>
        </FilterSection>

        <FilterSection title="Buying Intent" icon={ShoppingCartIcon}>
          <div className="space-y-3 pt-2">
            <div className="flex items-center gap-1 text-sm font-medium text-slate-700">
              Intent Topics <QuestionMarkCircleIcon className="h-4 w-4 text-slate-400" />
            </div>
            <p className="text-xs text-slate-500">Find customers looking for your solution!</p>
            <DButton variant="danger" className="w-full bg-red-600 hover:bg-red-700 text-white shadow-sm">
              Add intents
            </DButton>
          </div>
        </FilterSection>

        <FilterSection title="Revenue" icon={BanknotesIcon} isLocked>
          <div className="space-y-4">
             <div className="flex items-center gap-2">
                <DSelect options={revenueOptions} value={selectedMinRev} onChange={setSelectedMinRev} placeholder="Min" />
                <span className="text-slate-500 text-sm">to</span>
                <DSelect options={revenueOptions} value={selectedMaxRev} onChange={setSelectedMaxRev} placeholder="Max" />
             </div>
             <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <span className="text-sm font-medium text-slate-700">Include unknown revenue</span>
                <Switch checked={includeUnknownRevenue} onChange={setIncludeUnknownRevenue} className={`${includeUnknownRevenue ? 'bg-brand-600' : 'bg-slate-200'} relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1`}>
                  <span className={`${includeUnknownRevenue ? 'translate-x-5' : 'translate-x-1'} inline-block h-3 w-3 transform rounded-full bg-white transition-transform`} />
                </Switch>
             </div>
             <div className="space-y-2.5">
               {[
                 { name: 'Private', count: 0 },
                 { name: 'Public', count: 0 },
                 { name: 'Non Profit', count: 0 },
                 { name: 'Other', count: 0 }
               ].map((type) => (
                 <label key={type.name} className="flex items-center justify-between cursor-pointer group">
                   <div className="flex items-center gap-2.5">
                     <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 transition-colors cursor-pointer" />
                     <span className="text-sm text-slate-600 group-hover:text-slate-900 transition-colors">{type.name}</span>
                   </div>
                   <span className="text-xs font-medium text-slate-400">{type.count}</span>
                 </label>
               ))}
             </div>
          </div>
        </FilterSection>

        <FilterSection title="Funding" icon={CurrencyDollarIcon} isLocked>
           <div className="space-y-4">
             <div>
               <label className="block text-xs font-medium text-slate-700 mb-1.5">Select last funding amount</label>
               <div className="flex items-center gap-2">
                  <DSelect options={revenueOptions} value={selectedMinFunding} onChange={setSelectedMinFunding} placeholder="Min" />
                  <span className="text-slate-500 text-sm">to</span>
                  <DSelect options={revenueOptions} value={selectedMaxFunding} onChange={setSelectedMaxFunding} placeholder="Max" />
               </div>
             </div>
             <div>
               <label className="block text-xs font-medium text-slate-700 mb-1.5">Select total funding amount</label>
               <div className="flex items-center gap-2">
                  <DSelect options={revenueOptions} value={selectedMinFunding} onChange={setSelectedMinFunding} placeholder="Min" />
                  <span className="text-slate-500 text-sm">to</span>
                  <DSelect options={revenueOptions} value={selectedMaxFunding} onChange={setSelectedMaxFunding} placeholder="Max" />
               </div>
             </div>

             <div className="space-y-2.5 pt-3 border-t border-slate-100">
               <span className="text-sm font-medium text-slate-700 block mb-2">Stage</span>
               {['Pre seed', 'Seed', 'Series unknown', 'Series A', 'Series B', 'Series C', 'Series D', 'Series E-J'].map((stage) => (
                 <label key={stage} className="flex items-center gap-2.5 cursor-pointer group">
                   <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 transition-colors cursor-pointer" />
                   <span className="text-sm text-slate-600 group-hover:text-slate-900 transition-colors">{stage}</span>
                 </label>
               ))}
             </div>

             <div className="space-y-2.5 pt-3 border-t border-slate-100">
               <span className="text-sm font-medium text-slate-700 block mb-2">Other</span>
               {['Grant', 'Angel', 'Private equity', 'Debt financing', 'Non equity assistance', 'Post IPO equity', 'Undisclosed'].map((type) => (
                 <label key={type} className="flex items-center gap-2.5 cursor-pointer group">
                   <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 transition-colors cursor-pointer" />
                   <span className="text-sm text-slate-600 group-hover:text-slate-900 transition-colors">{type}</span>
                 </label>
               ))}
             </div>
           </div>
        </FilterSection>

        <FilterSection title="Technologies" icon={CpuChipIcon} isLocked>
          <div className="text-sm text-slate-500 p-2">Premium feature</div>
        </FilterSection>

        <FilterSection title="Founded Year" icon={CalendarDaysIcon}>
          <div className="text-sm text-slate-500 p-2">Select year range</div>
        </FilterSection>

        <FilterSection title="Headcount Growth" icon={ChartBarIcon}>
          <div className="space-y-4">
             <div>
                <div className="flex items-center gap-1 mb-1.5">
                  <label className="text-xs font-medium text-slate-700">Growth range (%)</label>
                  <QuestionMarkCircleIcon className="h-4 w-4 text-slate-400" />
                </div>
                <div className="flex gap-2">
                  <DInput placeholder="Enter #" />
                  <div className="flex items-center text-sm text-slate-500">to</div>
                  <DInput placeholder="Enter #" />
                </div>
             </div>
             <div>
               <label className="block text-xs font-medium text-slate-700 mb-1.5">Timeframe</label>
               <div className="flex items-center gap-2">
                 <span className="text-sm font-medium text-slate-600">Past</span>
                 <DInput placeholder="Select growth range first" disabled className="bg-slate-50 cursor-not-allowed" />
               </div>
             </div>
             <div>
               <label className="block text-xs font-medium text-slate-700 mb-1.5">Department</label>
               <DInput placeholder="Select a timeframe first" disabled className="bg-slate-50 cursor-not-allowed" />
             </div>
          </div>
        </FilterSection>

        <FilterSection title="Company Attributes" icon={IdentificationIcon}>
           <div className="text-sm text-slate-500 p-2">Select attributes</div>
        </FilterSection>

        <FilterSection title="Job Posting" icon={ClipboardDocumentIcon} isLocked>
           <div className="text-sm text-slate-500 p-2">Premium feature</div>
        </FilterSection>

        <FilterSection title="Headcount by Department" icon={BuildingOfficeIcon}>
           <div className="space-y-3">
             <DInput placeholder="Select department" />
             <div className="flex gap-2">
               <DInput placeholder="Min" />
               <div className="flex items-center text-sm text-slate-500">to</div>
               <DInput placeholder="Max" />
             </div>
           </div>
        </FilterSection>
      </div>
    </div>
  );
}